import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Clock, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import logo from '/media/Blue_Logo_Animated.mp4';

export default function MaintenancePage({ title, message, endTime, onRefresh }) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Calculate time remaining
  useEffect(() => {
    if (!endTime) return;

    const calculateTimeLeft = () => {
      const end = new Date(endTime);
      const now = new Date();
      const diff = end - now;

      if (diff <= 0) {
        return null;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      // Auto-refresh when countdown ends
      if (remaining === null && onRefresh) {
        onRefresh();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime, onRefresh]);

  const handleRefresh = async () => {
    setRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    // Small delay to show spinner
    setTimeout(() => setRefreshing(false), 1000);
  };

  const formatTimeLeft = () => {
    if (!timeLeft) return 'Wkrótce';

    const parts = [];
    if (timeLeft.hours > 0) {
      parts.push(`${timeLeft.hours}h`);
    }
    if (timeLeft.minutes > 0 || timeLeft.hours > 0) {
      parts.push(`${timeLeft.minutes}m`);
    }
    parts.push(`${timeLeft.seconds}s`);

    return parts.join(' ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="shadow-2xl border-0 overflow-hidden">
          <CardContent className="p-8 text-center">
            {/* Animated Logo */}
            <div className="relative mb-6">
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 20px rgba(59, 130, 246, 0.3)',
                    '0 0 40px rgba(59, 130, 246, 0.5)',
                    '0 0 20px rgba(59, 130, 246, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-white"
              >
                <video
                  src={logo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </div>

            {/* Maintenance Icon */}
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-6"
            >
              <Wrench className="w-8 h-8 text-amber-600" />
            </motion.div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-slate-900 mb-3">
              {title || 'Przerwa techniczna'}
            </h1>

            {/* Message */}
            <p className="text-slate-600 mb-6">
              {message || 'Przepraszamy za utrudnienia. Pracujemy nad ulepszeniem platformy. Wrócimy wkrótce!'}
            </p>

            {/* Countdown */}
            {endTime && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-slate-500 mb-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Szacowany czas powrotu</span>
                </div>
                <motion.div
                  key={timeLeft?.seconds}
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  className="text-3xl font-mono font-bold text-blue-600"
                >
                  {formatTimeLeft()}
                </motion.div>
              </div>
            )}

            {/* Refresh Button */}
            <Button
              variant="outline"
              size="lg"
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-full"
            >
              {refreshing ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                </motion.div>
              ) : (
                <RefreshCw className="w-5 h-5 mr-2" />
              )}
              {refreshing ? 'Sprawdzanie...' : 'Sprawdź ponownie'}
            </Button>

            {/* Footer note */}
            <p className="text-xs text-slate-400 mt-6">
              Strona odświeży się automatycznie po zakończeniu prac
            </p>
          </CardContent>
        </Card>

        {/* Support info */}
        <p className="text-center text-sm text-slate-500 mt-4">
          Pytania? Kontakt: kontakt@kconnect.pl
        </p>
      </motion.div>
    </div>
  );
}
